/**
 * Ross per-region resources, moved out of NewDeploymentPage so the infrastructure service can build on the same
 * data. The page and the mock service both read it from here: one copy, no drift.
 */
import type { RegionIssue } from '@/types/infrastructure'
import type { Environment } from '@/types'

// Ross per-region resources. Selecting a region auto-fills the Resource Group,
// VNet, Subnet and SQL Server (all read-only). These mirror the Terraform
// `region_config` map (single source of truth) — the portal sends `location`
// and the pipeline resolves the rest, so what's shown here is what gets used.
export interface RossRegionResource {
  rg: string
  vnet: string
  subnet: string
  sql: string
  location: string      // Azure location code passed to the pipeline
  issues: RegionIssue[] // known readiness issues; empty = fully ready. Surfaced at
                        // region-selection time so the user sees problems up front
                        // instead of hitting them mid-deployment.
}
// Values mirror the Terraform region_config (single source of truth), VERIFIED
// against prod subscription 9d97fe85 on 2026-08-12. `issues` reflects the current
// network/SQL readiness of each region; deployments are never blocked — the notes
// just tell the user what will need fixing.
const ROSS_REGION_RESOURCES_PROD: Record<string, RossRegionResource> = {
  'Central US':     { rg: 'RG-CENTRALUS-PRD-ROSS',    vnet: 'vnet-centralus-ross-prod',   subnet: 'default',                        sql: 'sqlsrv-centralus-prd-db-001',       location: 'centralus',     issues: [
    { level: 'warning', message: 'The deployment controller cannot yet reach this region’s app subnet (10.0.0.0/16 address overlap). The Ansible bootstrap stage will fail until the network team adds routing to the app subnet.' },
  ] },
  'West US':        { rg: 'RG-westus-prd-ross',        vnet: 'vnet-westus-prd-ross',       subnet: 'snet-westus-prd-infradeployment', sql: 'sqlsrv-westus-prd-001',             location: 'westus',        issues: [] },
  'East US 2':      { rg: 'ROSSSAASPUBLIC',            vnet: 'RossSaaSProd',               subnet: 'snet-eastus2-prd-infradeployment', sql: 'sqlsrv-eastus2-prd-ross-001',      location: 'eastus2',       issues: [
    { level: 'blocker', message: 'This region is not configured in the deployment pipeline (Terraform region_config) yet — the infrastructure step will fail. Ask the platform team to add it.' },
  ] },
  'North Europe':   { rg: 'rg-northeu-prd-ross',       vnet: 'vnet-northeu-prd-ross',      subnet: 'snet-prd-northeu-ross',          sql: 'sqlsrv-northeu-prd-ross-001',       location: 'northeurope',   issues: [
    { level: 'warning', message: 'VNet is not yet peered to the deployment controller. The Ansible bootstrap stage will fail until peering is added.' },
  ] },
  'West Europe':    { rg: 'rg-westeu-prd-ross',        vnet: 'vnet-westeu-prd-ross',       subnet: 'snet-infra-deployment-pipeline', sql: '',                                 location: 'westeurope',    issues: [
    { level: 'warning', message: 'VNet is not yet peered to the deployment controller. The Ansible bootstrap stage will fail until peering is added.' },
    { level: 'blocker', message: 'No SQL server exists in this region. Turn OFF “Create database”, or the database step will fail.' },
  ] },
  'Canada Central': { rg: 'Rg-canadacentral-prd-ross', vnet: 'vnet-canadacentral-prd-ross', subnet: 'snet-canadacentral-prd-ross',   sql: 'sqlsrv-canadacentral-prd-ross-001', location: 'canadacentral', issues: [] },
  'Canada East':    { rg: 'Rg-canadaeast-prd-ross',    vnet: 'vnet-canadaeast-prd-ross',   subnet: 'snet-canadaeast-prd-ross',       sql: '',                                  location: 'canadaeast',    issues: [
    { level: 'blocker', message: 'No SQL server exists in this region. Turn OFF “Create database”, or the database step will fail.' },
  ] },
}

// Dev/test environments — only Central US is provisioned
const ROSS_REGION_RESOURCES_DEV: Record<string, RossRegionResource> = {
  'Central US': { rg: 'rg-centralus-tst-ross', vnet: 'vnet-centralus-tst-ross01', subnet: 'snet-centralus-tst-ross01', sql: 'sqlsrv-centralus-tst-db-001', location: 'centralus', issues: [] },
}

const NON_PROD_ENVS = new Set<Environment>(['dev', 'stg', 'qa', 'uat', 'demo'])

export function getRossRegionResources(env: Environment | string) {
  return NON_PROD_ENVS.has(env as Environment) ? ROSS_REGION_RESOURCES_DEV : ROSS_REGION_RESOURCES_PROD
}
